'use client';

import React, { useActionState, useEffect, useRef, useState, useCallback } from 'react';
import { submitContactForm, type FormState } from '@/lib/actions/contact';
import { contactFormSchema } from '@/lib/schemas/contact';
import { useFormStatus } from 'react-dom';
import { Button, LoadingSpinner } from '@/components/ui/design-system';
import FormHeader from './FormHeader';
import FormFields from './FormFields';

interface ContactFormProps {
  selectedProduct?: 'fridgeBottle' | 'makhanaJar' | null;
}


/**
 * ContactForm Component
 * Form component using the shared ContactService through a custom hook
 */
function SubmitButton() {
  const { pending } = useFormStatus();

  return (
    <Button type="submit" variant="primary" size="lg" disabled={pending} className="w-full">
      {pending ? <LoadingSpinner /> : 'Submit Inquiry'}
    </Button>
  );
}

export default function ContactForm({ selectedProduct }: ContactFormProps) {
  const initialState: FormState = { message: '', errors: {} };
  const [state, dispatch] = useActionState(submitContactForm, initialState);
  const formRef = useRef<HTMLFormElement>(null);
  const [errors, setErrors] = useState<FormState['errors']>({});
  const [displayMessage, setDisplayMessage] = useState('');
  const [resetKey, setResetKey] = useState(0);
  const [internalSelectedProduct, setInternalSelectedProduct] = useState(selectedProduct);

  useEffect(() => {
    setInternalSelectedProduct(selectedProduct);
  }, [selectedProduct]);

  const validateField = useCallback((name: string, value: string) => {
    try {
      const fieldValidation = contactFormSchema.shape[name as keyof typeof contactFormSchema.shape];
      if (fieldValidation) {
        fieldValidation.parse(value);
        setErrors(prev => ({ ...prev, [name]: undefined }));
      }
    } catch (error: any) {
      if (error.errors) {
        setErrors(prev => ({ ...prev, [name]: error.errors.map((e: any) => e.message) }));
      }
    }
  }, []);

  const handleBlur = useCallback((e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    validateField(name, value);
  }, [validateField]);

  const handleValueChange = useCallback((name: string, value: string) => {
    validateField(name, value);
  }, [validateField]);

  useEffect(() => {
    if (state.message) {
      setDisplayMessage(state.message);
      if (state.message.startsWith('Success')) {
        formRef.current?.reset();
        setErrors({});
        setInternalSelectedProduct(undefined);
        setResetKey(prev => prev + 1); // Force re-render of form fields
        const timer = setTimeout(() => {
          setDisplayMessage('');
        }, 5000); // Hide message after 5 seconds
        return () => clearTimeout(timer);
      }
    }
    if (state.errors) {
      setErrors(state.errors);
    }
  }, [state]);

  return (
    <div className="max-w-lg mx-auto">
      {/* Form Header */}
      <FormHeader />
      
      <form noValidate ref={formRef} action={dispatch} className="space-y-6 mt-8">
                <FormFields key={resetKey} errors={errors || {}} selectedProduct={internalSelectedProduct} onBlur={handleBlur} onValueChange={handleValueChange} />
        <SubmitButton />
        {displayMessage && (
          <div
            className={`mt-4 p-3 rounded-md ${state.errors ? 'bg-red-50 border border-red-200' : 'bg-green-50 border border-green-200'}`}
            role={state.errors ? 'alert' : 'status'}
            aria-live={state.errors ? 'assertive' : 'polite'}
            aria-atomic="true"
          >
            <p className={`text-sm ${state.errors ? 'text-red-700' : 'text-green-700'}`}>
              {displayMessage}
            </p>
            {state.errors && (
              <p className="text-xs text-red-600 mt-1">
                Please correct the errors above and try again.
              </p>
            )}
          </div>
        )}
      </form>
    </div>
  );
} 